import { useEffect, useState } from "react";
import {
  hasStoredPreferences,
  loadPreferences,
  onPreferencesChanged,
  type WebPreferences,
} from "@/shared/preferences/store";
import {
  loadFirstRunState,
  onFirstRunChanged,
  type FirstRunState,
} from "@/shared/onboarding/first-run";
import { productLocaleForCountry } from "./document-language.mjs";
import { shouldUseStoredExperience } from "./experience-precedence.mjs";

export function useProductPreferences(): WebPreferences {
  const [preferences, setPreferences] = useState<WebPreferences>(() => loadPreferences());

  useEffect(() => onPreferencesChanged(() => setPreferences(loadPreferences())), []);

  return preferences;
}

export function useProductLocale(country?: string): string {
  const preferences = useProductPreferences();
  return productLocaleForCountry(preferences.lang, country || preferences.country);
}

export function useMarketingExperience(options: { preferFirstRun?: boolean } = {}): {
  country: string;
  lang: string;
  locale: string;
} {
  const preferences = useProductPreferences();
  const [firstRun, setFirstRun] = useState<FirstRunState>(() => loadFirstRunState());

  useEffect(() => onFirstRunChanged(() => setFirstRun(loadFirstRunState())), []);

  // Keep an in-progress onboarding choice authoritative across marketing and
  // auth routes. Otherwise following "I already have an account" can snap the
  // page back to an older persisted market before the user submits setup.
  const usePreferences = shouldUseStoredExperience({
    preferFirstRun: options.preferFirstRun,
    firstRunCompleted: firstRun.completed,
    storedPreferencesAvailable: hasStoredPreferences(),
  });
  const country = usePreferences ? preferences.country : firstRun.country;
  const lang = usePreferences ? preferences.lang : firstRun.lang;
  return {
    country,
    lang,
    locale: productLocaleForCountry(lang, country),
  };
}
